import { useState, useEffect, useCallback } from "react";
import { getContainerClientRegisteredInfo } from "../../../services/container";
import { parseCommentsList } from "../../../shared/utils/commentAuthor";

/**
 * Hook pour gérer les données client d'une ligne de collection
 */
export const useCollectionRowClient = (
  timeLine,
  dropOff,
  superContainers,
  corporationClients,
  historyCycle,
  _imageData,
  _azureUrl,
) => {
  const [clientRegisteredWeight, setClientRegisteredWeight] = useState(null);
  const [wasteName, setWasteName] = useState("");
  const [comments, setComments] = useState([]);
  const [checkStatus, setCheckStatus] = useState("-");
  const [rawCheckStatus, setRawCheckStatus] = useState(null);
  const [tags, setTags] = useState([]);
  const [wasteCategoryName, setWasteCategoryName] = useState("{}");
  const [clientName, setClientName] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [files, setFiles] = useState([]);
  const [toCharacterization, setToCharacterization] = useState(false);

  const fetchContainerClientRegisteredInfo = useCallback(
    async (id, signal) => {
      if (superContainers && Object.keys(superContainers).length > 0) {
        if (superContainers.containerObjectIdList.includes(id)) {
          id = superContainers._id;
        }
      }

      if (dropOff !== "" && timeLine._time) {
        try {
          const response = await getContainerClientRegisteredInfo(
            {
              start: new Date(new Date(dropOff).getTime() - 1).toISOString(),
              stop: new Date(
                new Date(timeLine._time).getTime() + 1,
              ).toISOString(),
            },
            id,
            signal,
          );

          if (Object.keys(response?.data || {}).length > 0) {
            const clientData = response?.data?.client
              ? JSON.parse(response?.data?.client)
              : {};
            const clientFromCorporation = corporationClients.find(
              (item) => item._id === response?.data?.mongoClientId,
            );
            setClientId(response?.data?.mongoClientId ?? "");
            setClientName(
              clientFromCorporation?.name ?? clientData?.name ?? "",
            );
            setClientAddress(
              clientFromCorporation?.address ?? clientData?.address ?? "",
            );
            setClientCode(clientData?.code ?? "");

            setFiles(() => clientData.files || []);

            setCheckStatus(clientData?.checkStatus ?? "No status");
            setRawCheckStatus(clientData?.checkStatus ?? null);

            setTags(clientData?.tags ?? []);

            setComments(parseCommentsList(response?.data?.comments));

            const wasteCategoryData = response?.data?.waste_category;
            if (
              wasteCategoryData &&
              wasteCategoryData !== "" &&
              wasteCategoryData !== "{}"
            ) {
              setWasteCategoryName(wasteCategoryData);

              try {
                const parsed = JSON.parse(wasteCategoryData);
                setWasteName(parsed.wasteCategoryName);
              } catch (e) {
                console.error("Erreur lors du parsing de waste_category :", e);
                setWasteName(timeLine.wasteName);
              }
            } else {
              setWasteCategoryName("");
              setWasteName(timeLine.wasteName);
            }

            setClientRegisteredWeight(response?.data?.weight / 1000);
          } else {
            setWasteName(timeLine.wasteName);
            setRawCheckStatus(null);
          }
        } catch (error) {
          if (
            error?.name === "CanceledError" ||
            error?.code === "ERR_CANCELED"
          ) {
            return;
          }
          setWasteName(timeLine.wasteName);
          console.error(
            "Error fetching container client registered info",
            error,
          );
        }
      } else {
        setWasteName(timeLine.wasteName);
      }
    },
    [timeLine, dropOff, superContainers, corporationClients],
  );

  useEffect(
    () => {
      if (!timeLine._id || !dropOff) return;

      let effectiveId = timeLine._id;
      if (
        superContainers &&
        Object.keys(superContainers).length > 0 &&
        superContainers.containerObjectIdList?.includes(effectiveId)
      ) {
        effectiveId = superContainers._id;
      }

      const controller = new AbortController();
      fetchContainerClientRegisteredInfo(effectiveId, controller.signal);
      return () => controller.abort();
    },
    // eslint-disable-next-line
    [timeLine._id, dropOff],
  );

  useEffect(() => {
    if (!historyCycle || historyCycle.length === 0) {
      setToCharacterization(false);
      return;
    }

    const hasCharacterization = historyCycle.some(
      (entry) => entry?.characterization === true,
    );
    setToCharacterization(hasCharacterization);
  }, [historyCycle]);

  return {
    clientRegisteredWeight,
    setClientRegisteredWeight,
    wasteName,
    setWasteName,
    comments,
    setComments,
    checkStatus,
    setCheckStatus,
    rawCheckStatus,
    setRawCheckStatus,
    tags,
    setTags,
    wasteCategoryName,
    setWasteCategoryName,
    clientName,
    setClientName,
    clientCode,
    setClientCode,
    clientId,
    setClientId,
    clientAddress,
    setClientAddress,
    files,
    setFiles,
    toCharacterization,
    setToCharacterization,
    fetchContainerClientRegisteredInfo,
  };
};
